/** `BrowserRouter`'s basename: present in `window.location`, absent from a `to`. */
export const APP_BASENAME = '/app';
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Backslashes are normalised to forward slashes by browsers when resolving a
 * URL, so `/\evil.com` and `\\evil.com` are protocol-relative in disguise.
 */
const BACKSLASH = /\\/;

/**
 * Browsers delete tabs and newlines anywhere in a URL before parsing it, so
 * `/<tab>/evil.com` is `//evil.com` by the time it is followed. Removed up front
 * so every check below sees what the browser will.
 */
const TAB_OR_NEWLINE = /[\t\n\r]/g;

export type ResolvedInAppNotificationUrl =
  | { kind: 'internal', to: string, }
  | { kind: 'external', href: string, }
  | { kind: 'none', };

const NONE: ResolvedInAppNotificationUrl = { kind: 'none' };

const resolveConversationPath = (path: string, referenceId?: number | null): ResolvedInAppNotificationUrl => {
  const parsed = new URL(path, 'https://notification.invalid');
  const action = parsed.pathname.match(/^\/(?:app\/)?actions\/(\d+)\/?$/);
  if (action && /^\d+$/.test(parsed.searchParams.get('conversation') ?? '')) {
    return { kind: 'internal', to: `/actions/${action[1]}${parsed.search}${parsed.hash}` };
  }
  const conversation = parsed.pathname.match(/^\/(?:luma\/)?company\/conversations\/(\d+)(?:\/popup)?\/?$/) ||
    parsed.pathname.match(/^\/(?:app\/)?inbox\/(\d+)\/?$/);
  if (!conversation) {
    return referenceId && Number.isSafeInteger(referenceId) && referenceId > 0
      ? { kind: 'internal', to: `/inbox/${referenceId}` } : NONE;
  }
  return { kind: 'internal', to: `/inbox/${conversation[1]}${parsed.search}${parsed.hash}` };
};

const isOtherCompanyHost = (host: string, currentHost: string) => {
  if (host === currentHost) {
    return false;
  }
  const parent = host.split('.').slice(1).join('.');
  const currentParent = currentHost.split('.').slice(1).join('.');
  return parent !== '' && (parent === currentParent || parent === currentHost);
};

/**
 * Classifies a notification's `url` so it can be followed safely.
 *
 * The producing service validates `url` at enqueue time and rejects
 * protocol-relative values, but the client does not rely on that: the content
 * originates outside this app, so every branch here is decided locally.
 *
 * This is the only gate between `url` and an `href`: the notification row and its
 * open-in-new-tab control are real links built from the result, so a
 * `javascript:` or protocol-relative value must resolve to `none` here — the
 * row then renders no link at all.
 */
export const resolveInAppNotificationUrl = (
  url: string | null | undefined,
  referenceType?: string | null,
  referenceId?: number | null,
  currentHost: string = globalThis.location?.host ?? '',
): ResolvedInAppNotificationUrl => {
  if (!url) {
    return referenceType === 'conversation' ? resolveConversationPath('/', referenceId) : NONE;
  }

  const trimmed = url.replace(TAB_OR_NEWLINE, '').trim();

  if (!trimmed || BACKSLASH.test(trimmed)) {
    return NONE;
  }

  if (trimmed.startsWith('/')) {
    // `BrowserRouter` is mounted with basename `/app`, so react-router prefixes
    // it for us — a path that already carries it would resolve to `/app/app/…`.
    const path = trimmed.startsWith(`${APP_BASENAME}/`) || trimmed === APP_BASENAME
      ? trimmed.slice(APP_BASENAME.length) || '/'
      : trimmed;

    // Checked after stripping the basename, so `/app//evil.com` cannot slip
    // through: react-router renders a `//` path as an off-site href as-is.
    if (path.startsWith('//')) {
      return NONE;
    }

    return referenceType === 'conversation'
      ? resolveConversationPath(path, referenceId) : { kind: 'internal', to: path };
  }

  // Parsed rather than prefix-matched so casing and escape tricks
  // (`JavaScript:`, embedded control characters) cannot slip past.
  try {
    const parsed = new URL(trimmed);

    if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
      return NONE;
    }

    if (referenceType === 'conversation') {
      const conversation = resolveConversationPath(`${parsed.pathname}${parsed.search}${parsed.hash}`, referenceId);
      return conversation.kind === 'internal' && isOtherCompanyHost(parsed.host, currentHost)
        ? { kind: 'external', href: `${parsed.origin}${APP_BASENAME}${conversation.to}` }
        : conversation;
    }
    return { kind: 'external', href: parsed.toString() };
  } catch {
    return NONE;
  }
};
